import type {
  LyricsLine,
  LyricsResult,
  ReaderBlock,
  ReaderContent
} from '@/types/reader'
import { READER_PROCESSOR_VERSION } from './constants'
import { buildGlossaryFromTokens } from './glossary'
import { hasLrcTimestamps, parseLrc } from './lrc'
import { normalizeReaderWhitespace } from './text'
import { tokenizeText, type DictionaryLookup } from './tokenizer'

function plainLyricsToLines(text: string): LyricsLine[] {
  return normalizeReaderWhitespace(text)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((text) => ({ startTime: 0, text }))
}

const TRADITIONAL_HINT_RE =
  /[優聽湯顯樂隊準備賣顆氣遲讓彈結夢時組個與夥飛裡覺說錯決頭斷還灣臺體學國會為這後]/
const SIMPLIFIED_HINT_RE =
  /[优听汤显乐队准备卖颗气迟让弹结梦时组个与伙飞里觉说错决头断还湾台体学国会为这后]/

function detectSourceScript(text: string) {
  const hasTraditional = TRADITIONAL_HINT_RE.test(text)
  const hasSimplified = SIMPLIFIED_HINT_RE.test(text)
  if (hasTraditional && hasSimplified) return 'mixed'
  if (hasTraditional) return 'traditional'
  if (hasSimplified) return 'simplified'
  return 'unknown'
}

export function normalizeLyricsResultLines(result: LyricsResult): LyricsLine[] {
  if (result.syncedLyrics?.length) return result.syncedLyrics

  if (result.plainLyrics && hasLrcTimestamps(result.plainLyrics)) {
    return parseLrc(result.plainLyrics)
  }

  return plainLyricsToLines(result.plainLyrics ?? '')
}

export function lyricsResultToReaderContent(
  input: {
    id: string
    title: string
    subtitle?: string
    videoId?: string
    artist?: string
    duration?: number
    lyricsOffset?: number
    result: LyricsResult
  },
  lookup: DictionaryLookup = new Map()
): ReaderContent {
  const lines = normalizeLyricsResultLines(input.result)
  const sourceScript = detectSourceScript(
    lines.map((line) => line.text).join('\n')
  )
  const blocks: ReaderBlock[] = lines.map((line, index) => ({
    id: `line-${index + 1}`,
    type: 'line',
    originalText: line.text,
    startTime: line.startTime,
    endTime: line.endTime,
    tokens: tokenizeText(line.text, lookup)
  }))

  const entriesById = new Map(
    [...lookup.values()].flat().map((entry) => [entry.id, entry] as const)
  )

  return {
    id: input.id,
    sourceType: 'lyrics',
    title: input.title,
    subtitle: input.subtitle ?? input.artist,
    metadata: {
      artist: input.artist,
      videoId: input.videoId,
      duration: input.duration ?? input.result.duration,
      providerTrackId: input.result.providerTrackId,
      lyricsProvider: input.result.source,
      lyricsOffset: input.lyricsOffset ?? 0,
      sourceScript,
      attribution: input.result.attribution,
      providerUrl: input.result.providerUrl
    },
    blocks,
    glossary: buildGlossaryFromTokens(
      blocks.flatMap((block) => block.tokens),
      entriesById
    ),
    processorVersion: READER_PROCESSOR_VERSION
  }
}

export function pastedLyricsToResult(text: string): LyricsResult {
  if (hasLrcTimestamps(text)) {
    return {
      source: 'manual',
      syncedLyrics: parseLrc(text),
      plainLyrics: text,
      confidence: 1
    }
  }

  return {
    source: 'manual',
    plainLyrics: text,
    confidence: 1
  }
}
